import { Button, Card } from '@iwoca/orion';
import styles from './NewDocUploads.module.css';
import messages from './NewDocUploads.messages';
import { FormattedMessage, useIntl } from 'react-intl';
import { intlFormatCurrency } from '@iwIntlProvider/FormattedCurrency';
import { useStateKey } from '../../../../iwQueryClient/useStateKey';
import {
    useGetApplication,
    useGetCustomerState,
    useGetProductOffers
} from '@api/lendingAPI/lapiHooks';
import BankStatements from './RequirementTypes/BankStatements/BankStatements';
import { TDataRequirements, TUKSupportedDocument } from './NewDocUploads.types';
import VATReturns from './RequirementTypes/VATReturns';
import CompanyAccounts from './RequirementTypes/CompanyAccounts';
import ManagementAccounts from './RequirementTypes/ManagementAccounts';
import SelfAssessmentTaxReturn from './RequirementTypes/SelfAssessmentTaxReturn';
import AgedDebtorReport from './RequirementTypes/AgedDebtorReport';
import { useEffect, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import {
    getRemainingDataRequirements,
    getRequiredDataRequirements
} from './NewDocUploads.helpers';
import { PutStateRequestBody, fetchPutState } from '@iwoca/lapi-client/edge';
import { produce } from 'immer';
import { useHistory } from 'react-router-dom';
import { tracking } from '@iwoca/frontend-tracking-library';
import { getQueryParameter } from '@iwRouter/LocationHelper';
import { getCurrentLocation } from '@iwRouter/HistoryProvider';
import { connect } from 'react-redux';
import { Dispatch } from 'redux';
import { SET_EDITING_AMOUNT } from '../../ApplyFlexiLoanView.actions';
import { LoadingAnimation } from '../../../ui/loadingAnimation/LoadingAnimation';

const UK_SUPPORTED_DOCUMENTS: TUKSupportedDocument[] = [
    'bank_statement',
    'vat_return',
    'company_accounts',
    'management_accounts',
    'self_assessment_tax_return',
    'aged_debtor_report'
];

const DOCUMENT_REQUIREMENT_COMPONENT_MAP: { [key: string]: React.ElementType } =
    {
        bank_statement: BankStatements,
        vat_return: VATReturns,
        company_accounts: CompanyAccounts,
        management_accounts: ManagementAccounts,
        self_assessment_tax_return: SelfAssessmentTaxReturn,
        aged_debtor_report: AgedDebtorReport
    };

const mapDispatchToProps = (dispatch: Dispatch) => ({
    showApplicationStep: () =>
        dispatch({
            type: SET_EDITING_AMOUNT
        })
});

type PropsFromRedux = ReturnType<typeof mapDispatchToProps>;

const NewDocUploads = ({ showApplicationStep }: PropsFromRedux) => {
    const stateKey = useStateKey();
    const [showStatus, setShowStatus] = useState(false);

    useEffect(() => {
        tracking.ampli.newDocUploadsPageViewed();
    }, []);

    const {
        data: productOffers,
        isLoading: isLoadingProductOffers,
        isError: isProductOffersError,
        refetch: refetchProductOffers,
        isFetching: isFetchingProductOffers
    } = useGetProductOffers(stateKey);

    const { data: customerState } = useGetCustomerState(stateKey);

    const history = useHistory();

    const saveCustomerState = useMutation({
        mutationFn: (state: PutStateRequestBody) =>
            fetchPutState({
                stateKey: stateKey,
                body: state
            }),
        onSuccess: () => {
            tracking.ampli.newDocUploadsResolvedAllRequirements();

            const stateKeyParam = history.location.search
                .substring(1)
                .split('&')
                .filter((entry) => entry.split('=')[0] === 'state_key');

            if (stateKeyParam) {
                history.push(`/?${stateKeyParam}`);
            } else {
                history.push('/');
            }
        },
        onError: () => {
            throw Error('Failed to update customer state');
        }
    });

    if (isLoadingProductOffers) return <NewDocUploadsLoading />;

    if (isProductOffersError) return null; // Todo: Do something better than returning null for errors

    const requiredDataRequirements = getRequiredDataRequirements(
        productOffers,
        UK_SUPPORTED_DOCUMENTS
    );

    const hasCompletedRequirements = !getRemainingDataRequirements(
        productOffers,
        UK_SUPPORTED_DOCUMENTS
    ).length;

    const submitApplication = async () => {
        tracking.ampli.newDocUploadsClickedSubmitApplication();

        const productOffersResponse = await refetchProductOffers();

        const remainingDataRequirements = getRemainingDataRequirements(
            productOffersResponse.data,
            UK_SUPPORTED_DOCUMENTS
        );

        if (!remainingDataRequirements.length) {
            const newCustomerState = produce(customerState, (draft) => {
                // @ts-expect-error Have to ignore this because UI is not included in the type for the /state PUT request body from LAPI client
                draft!.ui.hasCreatedFlexiApplication = false;
            });

            await saveCustomerState.mutateAsync({ data: newCustomerState! });
        } else {
            tracking.ampli.newDocUploadsRequirementsOutstanding();
        }

        setShowStatus(true);
    };

    return (
        <>
            <Card className={styles.docUploadsContainer}>
                <NewDocUploadsHeader onEditClick={showApplicationStep} />

                <p className="m-0 mb-xl text-s lg:text-m">
                    {hasCompletedRequirements ? (
                        <FormattedMessage {...messages.headingInfoCompleted} />
                    ) : (
                        <FormattedMessage {...messages.headingInfo} />
                    )}
                </p>

                <p className="m-0 mb-3xl text-s lg:text-m">
                    <FormattedMessage {...messages.saveApplicationSubheading} />
                </p>

                {requiredDataRequirements.map(
                    (requirement: TDataRequirements[number]) => {
                        if (
                            !DOCUMENT_REQUIREMENT_COMPONENT_MAP[
                                requirement.type!
                            ]
                        )
                            return null;

                        const RequirementDetails =
                            DOCUMENT_REQUIREMENT_COMPONENT_MAP[
                                requirement.type!
                            ];

                        return (
                            <RequirementDetails
                                key={requirement.type}
                                quantifier={requirement.quantifier}
                                quantifier_unit={requirement.quantifier_unit}
                                resolutionMethods={
                                    requirement.resolution_methods
                                }
                                status={requirement.status}
                                showStatus={showStatus}
                            />
                        );
                    }
                )}

                <div className={styles.submitCTAContainer}>
                    <Button
                        onClick={submitApplication}
                        type="button"
                        loading={
                            isFetchingProductOffers ||
                            saveCustomerState.isLoading
                        }
                    >
                        <FormattedMessage {...messages.submitCTA} />
                    </Button>
                </div>
            </Card>
        </>
    );
};

// eslint-disable-next-line import/no-default-export
export default connect(null, mapDispatchToProps)(NewDocUploads);

const NewDocUploadsHeader = ({ onEditClick }: { onEditClick?: () => void }) => {
    const intl = useIntl();
    const stateKey = useStateKey();
    const { data: application, isLoading } = useGetApplication(stateKey);

    if (isLoading) return null;

    const requested_product = application?.requested_products![0]!;

    const applicationAmount =
        requested_product.requested_cash_amount! || requested_product.amount;

    const isTopup = getQueryParameter(getCurrentLocation(), 'topup');

    return (
        <>
            <h1 className={styles.heading}>
                <FormattedMessage
                    {...(isTopup ? messages.topupHeading : messages.heading)}
                />
            </h1>

            <h3 className={styles.subheading}>
                <FormattedMessage
                    {...(isTopup
                        ? messages.topUpSubheading
                        : messages.subheading)}
                    values={{
                        amount: intlFormatCurrency({
                            intl: intl,
                            value: applicationAmount,
                            decimalPlaces: 2
                        })
                    }}
                />

                {onEditClick && (
                    <Button
                        variant="tertiary"
                        className={styles.editCTA}
                        onClick={() => {
                            tracking.ampli.newDocUploadsClickedEditAmount();
                            onEditClick();
                        }}
                        type="button"
                    >
                        Edit
                    </Button>
                )}
            </h3>
        </>
    );
};

const NewDocUploadsLoading = () => {
    return (
        <Card className={styles.docUploadsContainerLoading}>
            <NewDocUploadsHeader />
            <div className={styles.loadingContent}>
                <LoadingAnimation data-testid="loading-spinner" />
                <p className={styles.docsUploadLoadingMessage}>
                    <FormattedMessage {...messages.docsUploadLoading} />
                </p>
            </div>
        </Card>
    );
};
